import { Overrides } from '../../types';
import { aiAfterStyles, aiAnimatingStyles } from '../aiStyles';
import baseColors from '../colors';
import { gutters } from '../utils';

export const inputFieldOverrides: Overrides = {
  MuiFormControl: {
    styleOverrides: {
      root: ({ ownerState }) => ({
        '& .MuiSelect-select': {
          overflowX: 'auto !important',
          '::-webkit-scrollbar': {
            display: /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)
              ? undefined
              : 'none !important',
          },
        },

        '&, & *': {
          boxSizing: 'border-box',
        },
        '&[aria-disabled="false"] input[readonly]': {
          backgroundColor: baseColors.mono0,
        },

        '&.MuiFormControl-root.FDBTextInput--readOnly': {
          '& .MuiInputBase-root.Mui-readOnly.MuiInputBase-readOnly': {
            backgroundColor: 'transparent',
          },

          '&.FDBTextInput--isAI&::after, &.FDBTextInput--isAIAnimating&::after':
            {
              content: 'none',
            },
        },

        '&[aria-disabled="true"]': {
          cursor: 'not-allowed',

          '& .MuiOutlinedInput-input, & .MuiOutlinedInput-root': {
            backgroundColor: baseColors.mono300,
            color: baseColors.mono500,
            pointerEvents: 'none',
          },
          '& .MuiOutlinedInput-root .FDBIcon': {
            color: `${baseColors.mono500} !important`,
          },
        },

        // NumberField
        '&.FDBNumericInput.FDBNumericInput': {
          '& input': {
            textAlign: 'right',
          },
          '&--left-align input': {
            textAlign: 'left',
          },
        },

        // TextInput
        '&.FDBTextInput .MuiOutlinedInput-root': {
          backgroundColor: baseColors.mono0,

          '& input': {
            borderRadius: '4px',
          },

          // TextArea
          '&.MuiInputBase-multiline': {
            paddingTop: '10px',
            paddingBottom: '10px',
          },
        },

        '&.FDBTextInput[aria-disabled="true"] .MuiOutlinedInput-root': {
          backgroundColor: baseColors.mono300,
        },

        '&.FDBTextInput--isAI, &.FDBTextInput--isAIAnimating': {
          zIndex: 0,

          '&[aria-disabled="true"]&::after': {
            content: 'none',
          },

          '& .MuiOutlinedInput-root:hover:not(.Mui-disabled) > fieldset, .MuiOutlinedInput-root.Mui-focused:not(.Mui-disabled) > fieldset, & .MuiInputBase-root > fieldset':
            {
              borderColor: `${
                !ownerState?.error ? 'transparent' : '""'
              } !important`,
            },
        },

        '&.FDBTextInput--isAI&::after, &.FDBTextInput--isAIAnimating&::after': {
          display: `${ownerState?.error ? 'none' : undefined} `,
          borderRadius: '6px',
          ...aiAfterStyles,
        },

        '&.FDBTextInput--isAIAnimating&::after': {
          ...aiAnimatingStyles,
        },
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        position: 'relative',
        transform: 'none',
        whiteSpace: 'inherit',
      },
      asterisk: {
        // Hide MUI's built-in asterisk when required prop is true
        display: 'none',
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: {
        height: '100%',
        '& button': {
          background: 'transparent',
          border: '2px solid transparent',
          borderRadius: '50%',
          outline: 'none',
          cursor: 'pointer',
          padding: '3px',
        },
        '& button:focus': {
          border: `2px solid ${baseColors.teal300}`,
        },
        '&.MuiInputAdornment-positionStart': {
          marginRight: 0,
        },
        '&.MuiInputAdornment-positionEnd': {
          marginLeft: 0,
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        backgroundColor: baseColors.mono0,
        borderRadius: gutters[4],
        boxSizing: 'border-box',
        border: 'none !important',
        boxShadow: 'none !important',
        outline: 'none',
        '::placeholder': {
          color: baseColors.mono500,
          opacity: 1,
          fontWeight: 500,
        },
        '&.MuiInputBase-input': {
          color: baseColors.mono900,
        },
      },
      root: ({ ownerState }) => ({
        '&:focus': {
          outline: 'none',
          outlineOffset: 'none',
          border: 'none !important',
          boxShadow: 'none !important',
        },
        '&.Mui-focused:not(.Mui-disabled) > fieldset': {
          borderColor: `${
            (ownerState?.error && baseColors.red300) ||
            (ownerState?.readOnly && 'transparent') ||
            baseColors.purple700
          } !important`,
          borderWidth: '2px !important',
        },
        paddingLeft: 0,
        '& > fieldset': {
          borderStyle: 'solid !important',
          borderWidth: `${(ownerState?.error && '2px') || '1px'} !important`,
          borderColor: `${
            (ownerState?.error && baseColors.red300) ||
            (ownerState?.readOnly && 'transparent') ||
            baseColors.mono400
          }  !important`,
        },
        '& > fieldset > legend': {
          borderBottom: 'none',
          width: 'initial',
        },
        '&:hover:not(.Mui-disabled)': {
          '& > fieldset': {
            borderColor: `${
              (ownerState?.error && baseColors.red300) ||
              (ownerState?.readOnly && 'transparent') ||
              baseColors.purple700
            }   !important`,
            borderWidth: '2px !important',
          },
        },
        // FDBSelect
        '&.FDBSelect.MuiInputBase-root': {
          '& .MuiSelect-select': {
            cursor: 'pointer',
          },

          '&.FDBSelect--displayValuesAsChips .MuiSelect-multiple': {
            paddingLeft: 8,
            paddingTop: 8,
            paddingBottom: 8,
          },

          '&[aria-disabled="true"]': {
            cursor: 'not-allowed',
            backgroundColor: baseColors.mono300,

            '& > fieldset': {
              border: 'none !important',
            },
            '& .MuiSelect-select': {
              cursor: 'not-allowed',
              backgroundColor: baseColors.mono300,
            },
            '& .MuiSelect-icon': {
              color: baseColors.mono500,
            },
            // multi select display as text color
            '& .FDBBox': {
              color: baseColors.mono500,
            },
          },
        },
        '&.FDBSelect--error > fieldset': {
          border: `2px solid ${baseColors.red300} !important`,
        },
        '&.FDBSelect--error:hover > fieldset': {
          border: `2px solid ${baseColors.red300} !important`,
        },
        '&.MuiInputBase-adornedEnd:not(.MuiInputBase-adornedStart)': {
          paddingRight: 0,
        },
      }),
    },
  },
};
